import { useEffect, useMemo, useState } from "react";
import Layout from "../../../components/layouts/Layout";
import Section from "../../../components/base/Section";
import Indicator from "../../../components/base/Indicator";
import Icon from "../../../components/base/Icon";
import { clearToolbar, isRoot, setPage, setToolbarActions, useHub } from "../../../core/Global";
import { toastError } from "../../../components/base/Toast";
import MigrationCard from "../components/MigrationCard";
import MigrateDrawer from "../components/MigrateDrawer";
import RollbackModal from "../components/RollbackModal";
import MigrationRow from "../models/MigrationRow";
import "./MigrationView.css";


type Filter = "all" | "legacy" | "plan" | "inactive"


// Every customer with a running subscription, the one that runs out first at the top: who is
// still on the old subscription, who is on one of the new plans, and the way to move the first
// kind onto the second. Rinvox's own page -- root only.
export default function MigrationView() {

    const hub = useHub()

    const [rows, rowsSet] = useState<MigrationRow[]>([])
    const [loading, loadingSet] = useState<boolean>(true)
    const [filter, filterSet] = useState<Filter>("all")
    const [search, searchSet] = useState<string>("")
    const [moving, movingSet] = useState<MigrationRow>()
    const [rollingBack, rollingBackSet] = useState<MigrationRow>()

    const load = (silent: boolean = false) => {
        if (!silent) loadingSet(true)
        hub.Get("/system/migration/", res => {
            loadingSet(false)
            if (res.success) rowsSet(res.data ?? [])
            else toastError(res.message ?? "Couldn't load the customers.")
        }, () => loadingSet(false))
    }

    useEffect(() => {
        setPage("Migration")
        if (isRoot()) load()
        return () => { clearToolbar() }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    // The page's button lives in the header, beside its title.
    useEffect(() => {
        if (!isRoot()) return
        setToolbarActions(() => (
            <button type="button" className="btn btn-sm btn-light-primary" disabled={loading}
                onClick={() => load()}>
                <Icon icon="arrows-rotate" type="solid" size={12} className={"me-2" + (loading ? " fa-spin" : "")} />
                Refresh
            </button>
        ))
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loading])

    // Stopped accounts -- cancelled and run out, or suspended over a payment -- are not active
    // customers: they stay out of every count and filter but their own.
    const counts = useMemo(() => {
        const live = rows.filter(r => !r.isInactive)
        return {
            all: live.length,
            legacy: live.filter(r => r.isLegacy).length,
            plan: live.filter(r => !r.isLegacy).length,
            soon: live.filter(r => r.isLegacy && r.daysLeft <= 7).length,
            inactive: rows.length - live.length
        }
    }, [rows])

    const shown = useMemo(() => {
        const q = search.trim().toLowerCase()
        return rows
            .filter(r => filter === "inactive" ? !!r.isInactive
                : !r.isInactive && (filter === "all" || (filter === "legacy" ? r.isLegacy : !r.isLegacy)))
            .filter(r => !q || [r.name, r.username, r.company, r.email, r.phone]
                .some(v => (v ?? "").toLowerCase().includes(q)))
    }, [rows, filter, search])

    if (!isRoot()) {
        return (
            <Layout wide>
                <Section fluid className="py-20 text-center text-gray-600">
                    This page is for the root account only.
                </Section>
            </Layout>
        )
    }

    return (
        <Layout wide>
            <Section fluid className="py-6 migration-page">

                <div className="migration-stats">
                    <div className="migration-stat">
                        <div className="migration-stat-value">{counts.all}</div>
                        <div className="migration-stat-label">Active customers</div>
                    </div>
                    <div className="migration-stat is-legacy">
                        <div className="migration-stat-value">{counts.legacy}</div>
                        <div className="migration-stat-label">On the old subscription</div>
                    </div>
                    <div className="migration-stat is-plan">
                        <div className="migration-stat-value">{counts.plan}</div>
                        <div className="migration-stat-label">On a new plan</div>
                    </div>
                    <div className="migration-stat is-soon">
                        <div className="migration-stat-value">{counts.soon}</div>
                        <div className="migration-stat-label">Old ones renewing in 7 days</div>
                    </div>
                </div>

                <div className="migration-toolbar">
                    <div className="migration-filters">
                        {
                            ([["all", "All"], ["legacy", "Old subscription"], ["plan", "New plans"], ["inactive", "Cancelled / suspended"]] as [Filter, string][]).map(([key, label]) =>
                                <button key={key} type="button"
                                    className={"migration-filter" + (filter === key ? " is-active" : "")}
                                    onClick={() => filterSet(key)}>
                                    {label}
                                    <span>{counts[key]}</span>
                                </button>
                            )
                        }
                    </div>
                    <div className="migration-search">
                        <Icon icon="magnifying-glass" size={12} />
                        <input type="text" placeholder="Search name, company, email, phone"
                            value={search} onChange={e => searchSet(e.target.value)} />
                    </div>
                </div>

                {
                    loading ?
                        <div className="py-20"><Indicator /></div>
                        : shown.length === 0 ?
                            <div className="text-center text-gray-500 py-20">Nobody matches.</div>
                            :
                            <div className="migration-list">
                                {shown.map(r => <MigrationCard key={r.userId} row={r} onMove={() => movingSet(r)} onRollBack={() => rollingBackSet(r)} />)}
                            </div>
                }

            </Section>

            {
                moving &&
                <MigrateDrawer
                    row={moving}
                    onClose={() => movingSet(undefined)}
                    onMoved={() => load(true)} />
            }

            {
                rollingBack &&
                <RollbackModal
                    row={rollingBack}
                    onClose={() => rollingBackSet(undefined)}
                    onDone={() => load(true)} />
            }
        </Layout>
    )
}
